import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

interface SeoProps {
  title: string;
  description: string;
  path?: string;
  keywords?: string;
  image?: string;
  type?: "website" | "article" | "profile";
  /** Optional JSON-LD structured data objects (will be stringified) */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Defaults to true; set false to discourage indexing */
  index?: boolean;
}

const SITE_ORIGIN = "https://aranon.dev";
const DEFAULT_OG_IMAGE =
  "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/a1da418c-4d88-40da-9d7d-3c1c94dd75c9/id-preview-c08380d8--6859ace2-7595-4d16-94f2-4301f742150b.lovable.app-1776959822491.png";

const DEFAULT_KEYWORDS_EN =
  "AI software development, enterprise AI, AI consulting, AI for business, AI agents, AI automation, custom AI solutions, generative AI, LLM integration, AI strategy, AI for operations, AI for healthcare, AI for industry, AI for energy, AI for telecom, AI for franchise, AI venture studio, AI engineering, machine learning consulting, data engineering, MLOps, AI copilots, RAG, vector databases, AI platform, AI for procurement, AI for OPME, AI for Brazil, Brazilian AI company, Aranon, Aranon Technologies";

const DEFAULT_KEYWORDS_PT =
  "desenvolvimento de IA para empresas, software de inteligência artificial, consultoria de IA, IA empresarial, agentes de IA, automação com IA, IA generativa, IA personalizada, integração de LLM, estratégia de IA, IA para operações, IA para saúde, IA para indústria, IA para energia, IA para telecom, IA para franquias, venture studio de IA, engenharia de IA, machine learning, MLOps, copilotos de IA, RAG, bancos vetoriais, plataforma de IA, IA para licitações, IA para OPME, IA no Brasil, empresa brasileira de IA, Aranon, Aranon Technologies";

export const Seo = ({
  title,
  description,
  path = "",
  keywords,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
  index = true,
}: SeoProps) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").startsWith("pt") ? "pt-BR" : "en";
  const url = `${SITE_ORIGIN}${path}`;
  const altLangUrl = url; // same path, browser handles language detection
  const kw =
    keywords ||
    (lang === "pt-BR" ? DEFAULT_KEYWORDS_PT : DEFAULT_KEYWORDS_EN);

  const ldArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={kw} />
      <meta name="author" content="Aranon Technologies" />
      <meta
        name="robots"
        content={
          index
            ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
            : "noindex, nofollow"
        }
      />
      <meta name="googlebot" content={index ? "index, follow" : "noindex, nofollow"} />
      <link rel="canonical" href={url} />

      {/* Hreflang — bilingual site */}
      <link rel="alternate" hrefLang="en" href={altLangUrl} />
      <link rel="alternate" hrefLang="pt-BR" href={altLangUrl} />
      <link rel="alternate" hrefLang="x-default" href={altLangUrl} />

      {/* Open Graph */}
      <meta property="og:site_name" content="Aranon Technologies" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={lang === "pt-BR" ? "pt_BR" : "en_US"} />
      <meta property="og:locale:alternate" content={lang === "pt-BR" ? "en_US" : "pt_BR"} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@aranon" />

      {ldArray.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
};
